import React, { useRef, useState } from "react";
import styled from "styled-components";
import { contextMenuItemType } from "components/contextmenu/ContextMenu";
import i18n from "i18next";
import BanksIcon from "assets/images/banks.svg?react";
import CryptoWalletIcon from "assets/images/cryptowallet.svg?react";
import AiAppraiserIcon from "assets/images/ai_appraiser.svg?react";
import HomesIcon from "assets/images/homes.svg?react";
import CarsIcon from "assets/images/cars.svg?react";
import MetalsIcon from "assets/images/metals.svg?react";
import PortfolioLinkingIcon from "assets/images/portfolio_linking.svg?react";
import GenericMessageDialog from "components/dialog/GenericMessageDialog";
import { AI_SYNC_RELEASE_TS, isAmcUser, updateUserPreferences, userPreferencesSelector } from "@kubera/common";
import { useSelector, useDispatch } from "react-redux";
import RedDot from "components/indicators/RedDot";

const EmptyGridView = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 6px;
  margin-top: 17px;
  flex-wrap: wrap;
`;

const EmptyGridViewConnectAccountView = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: ${({ isColumnLayout }) => (!isColumnLayout ? "row" : "column")};
  min-height: ${props => (props.height ? props.height : "110px")};
  width: ${props => props.width};
  min-width: ${props => props.width};
  background: ${props => props.theme.emptyGridViewTitleBG};
  border: ${props => props.border || props.theme.emptyGridViewTitleBR};
  box-sizing: border-box;
  cursor: pointer;
  justify-content: space-between;
  &: hover {
    background: ${props => props.theme.emptyGridViewTitleBGHover};
  }
`;

const EmptyGridViewConnectAccountTitle = styled.div`
  font-family: "Roboto Mono";
  font-style: normal;
  font-weight: ${props => props.fontWeight || "700"};
  font-size: 12px;
  line-height: 120%;
  white-space: pre-wrap;
  padding: ${props => props.padding};
  margin: ${props => props.margin || "0"};
  align-self: ${props => (props.position === "top" ? "flex-start" : "flex-end")};
  text-transform: uppercase;
`;

const EmptyGridViewConnectAccountSubtitle = styled.div`
  flex-grow: 1;
  color: ${props => (props.isAddNew ? "rgba(0, 0, 0, 0.6)" : props.theme.emptyViewSubtitleColor)};
  font-feature-settings: "ss01" on;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  white-space: pre-wrap;
  padding: ${props => props.padding};
  margin: ${props => props.margin || "0"};
`;

const EmptyGridViewConnectAccountIcon = styled.div`
  outline: 0;
  padding: ${props => props.padding};
  cursor: pointer;
  align-self: ${props => (props.position === "top" ? "flex-start" : "flex-end")};
  text-align: ${props => (props.align === "left" ? "left" : "right")};
  margin-bottom: ${props => props.marginBottom || "-4px"};
`;

const EmptyGridViewConnectExtrasContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: ${props => props.height || "110px"};
  width: ${props => props.width || "240px"};
  background: ${props => props.theme.emptyGridViewTitleBG};
  border: ${props => props.theme.emptyGridViewTitleBR};
  box-sizing: border-box;
  cursor: pointer;
  justify-content: space-between;
  &: hover {
    background: ${props => props.theme.emptyGridViewTitleBGHover};
  }
`;

const EmptyGridViewConnectExtrasIcon = styled.div`
  outline: 0;
  padding: ${props => props.padding || "10px"};
  cursor: pointer;
  align-self: ${props => (props.position === "left" ? "flex-start" : "flex-end")};
  margin-bottom: -4px;
`;

const AddViewEnterManuallyTip = styled.div`
  width: 554px;
  margin-top: 6px;
  font-weight: 400;
  font-size: 12px;
  line-height: 27px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);

  a {
    color: rgba(0, 0, 0, 0.5);
    text-decoration: underline;
  }
`;

const EmptyGridViewManualItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const EmptyGridViewManualTitle = styled.div`
  margin-top: 3px;
  margin-left: 14px;
  opacity: 0.8;
  font-family: "Roboto Mono";
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  text-transform: uppercase;
`;

const EmptyGridViewManualDesc = styled(EmptyGridViewConnectAccountSubtitle)`
  padding: 14px 14px 0 14px;
  width: 150px;
  border-right: ${props =>
    `1px solid ${props.isAddNew ? "rgba(0, 0, 0, 0.1)" : props.theme.emptyGridViewManualBorder}`};
`;

const EmptyGridViewBottomRowContainer = styled.div`
  display: flex;
  height: 110px;
  margin-top: -2px;
  border: ${props => `1px solid ${props.isAddNew ? "rgba(0, 0, 0, 0.2)" : props.theme.emptyGridViewManualBorder}`};
  background: ${props => `${props.isAddNew ? "#FFFFFF" : props.theme.emptyGridViewManualBg}`};
`;

const BanksIconComponent = styled(BanksIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const CryptoWalletIconComponent = styled(CryptoWalletIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const AiAppraiserIconComponent = styled(AiAppraiserIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const HomesIconComponent = styled(HomesIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const CarsIconComponent = styled(CarsIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const MetalsIconComponent = styled(MetalsIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const PortfolioLinkingIconComponent = styled(PortfolioLinkingIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const EmptyGridViewTickerContainer = styled.div`
  width: ${props => props.width};
  flex-grow: ${props => props.grow || 0};
  display: flex;
  box-sizing: border-box;
  border: ${props => props.border || props.theme.emptyGridViewTitleBR};
`;

const EmptyGridViewAiImportView = styled(EmptyGridViewConnectAccountView)`
  max-width: 109px;
  flex-direction: column;
  border: none;
  border-left: ${props =>
    `1px dashed ${props.isAddNew ? "rgba(0, 0, 0, 0.2)" : props.theme.emptyGridViewManualBorder}`};
  align-items: flex-end;
  background: ${props => props.background || props.theme.emptyGridViewTitleBG};
`;

const ConnectTitleIconContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const EmptyGridViewLinkPortfoliosView = styled(EmptyGridViewConnectAccountView)`
  align-items: flex-end;
  height: 80px;
  width: 553px;
  padding: 14px;
  background: ${props => props.theme.emptyGridViewTitleBG};
  border: ${props => props.theme.emptyGridViewTitleBR};
  box-sizing: border-box;
  cursor: pointer;
  &: hover {
    background: ${props => props.theme.emptyGridViewTitleBGHover};
  }
`;

const DetailsLink = styled.a`
  margin-left: 2px;
  color: ${props => (props.isAddNew ? "rgba(0, 0, 0, 0.6)" : props.theme.emptyViewSubtitleColor)};
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const EmptyGridViewManualView = styled(EmptyGridViewConnectAccountView)`
  min-width: 150px;
  background: none;
  border-right: ${props =>
    props.borderRight || `1px solid ${props.isAddNew ? "rgba(0, 0, 0, 0.1)" : props.theme.emptyGridViewManualBorder}`};
`;

const AiSyncRedDot = styled(RedDot)`
  position: static;
  display: inline-block;
  margin-left: 4px;
  margin-bottom: 1px;
  margin-right: 0px;
  width: 6px;
  height: 6px;
`;

function EmptyGridViewAssetsComponent(props) {
  const dispatch = useDispatch();
  const userPreferences = useSelector(userPreferencesSelector);
  const isAmcUserFlag = useSelector(isAmcUser);

  const [showManualAssetTip, setShowManualAssetTip] = useState(false);

  const readOnlyWlClient = props.isReadOnlyWlClient;
  const aiImportDisabled = props.aiImportDisabled;
  const showAiSyncSubTiles = aiImportDisabled === false && isAmcUserFlag === false;

  const aiSyncRedDotRef = useRef(null);

  const handleAiSyncTileClick = () => {
    if (aiSyncRedDotRef.current && aiSyncRedDotRef.current.isRedDotShown() === true) {
      aiSyncRedDotRef.current.setPreferenceFlag(true);
    }
    props.handleInitialTipClick(contextMenuItemType.AI_SYNC);
  };

  return (
    <EmptyGridView className={props.className}>
      {readOnlyWlClient === false && (
        <EmptyGridViewTickerContainer width="342px">
          <EmptyGridViewConnectAccountView
            border="none"
            isColumnLayout={true}
            onClick={e => props.handleInitialTipClick(contextMenuItemType.LINK)}
          >
            <EmptyGridViewConnectAccountTitle position="top" padding="12px 0 0 14px ">
              {i18n.t("assetsComponent.emptyViewConnectBanksTitle")}
            </EmptyGridViewConnectAccountTitle>
            <ConnectTitleIconContainer>
              <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow} padding="0 0 12px 14px">
                {i18n.t("assetsComponent.emptyViewConnectBanksSubtitle")}
              </EmptyGridViewConnectAccountSubtitle>
              <EmptyGridViewConnectAccountIcon position="bottom" padding="0 14px 12px 0">
                <BanksIconComponent />
              </EmptyGridViewConnectAccountIcon>
            </ConnectTitleIconContainer>
          </EmptyGridViewConnectAccountView>
          {showAiSyncSubTiles && (
            <EmptyGridViewAiImportView isAddNew={props.isAddNewFlow} onClick={handleAiSyncTileClick}>
              <EmptyGridViewConnectAccountSubtitle
                isAddNew={props.isAddNewFlow}
                padding="14px 10px 0 10px"
                margin="35px 0 0 0"
              >
                {i18n.t("assetsComponent.emptyViewAiSyncSubtitle")}
              </EmptyGridViewConnectAccountSubtitle>
              <EmptyGridViewConnectAccountTitle position="top" padding="6px 10px 12px 10px" fontWeight="400">
                {i18n.t("assetsComponent.emptyViewAiSyncTitle")}
                <AiSyncRedDot
                  ref={aiSyncRedDotRef}
                  preference="isAiSyncTilesTipShown"
                  showIfUserBeforeThisTs={AI_SYNC_RELEASE_TS}
                />
              </EmptyGridViewConnectAccountTitle>
            </EmptyGridViewAiImportView>
          )}
        </EmptyGridViewTickerContainer>
      )}

      <EmptyGridViewTickerContainer
        width={readOnlyWlClient === true ? "550px" : props.isAddNewFlow ? "204px" : "252px"}
      >
        <EmptyGridViewConnectAccountView
          border="none"
          isColumnLayout={true}
          onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_STOCK)}
        >
          <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow} padding="12px 10px 10px 14px">
            {i18n.t("assetsComponent.emptyViewConnectStockSubtitle")}
          </EmptyGridViewConnectAccountSubtitle>
          <EmptyGridViewConnectAccountTitle position="top" padding="0 0 12px 14px">
            {i18n.t("assetsComponent.emptyViewConnectStockTitle")}
          </EmptyGridViewConnectAccountTitle>
        </EmptyGridViewConnectAccountView>
      </EmptyGridViewTickerContainer>

      {readOnlyWlClient === false && (
        <EmptyGridViewTickerContainer width="342px">
          <EmptyGridViewConnectAccountView
            border="none"
            onClick={e => props.handleInitialTipClick(contextMenuItemType.LINK_CRYPTO)}
          >
            <EmptyGridViewConnectAccountTitle position="top" padding="18px">
              {i18n.t("assetsComponent.emptyViewConnectCryptoTitle")}
            </EmptyGridViewConnectAccountTitle>
            <EmptyGridViewConnectAccountIcon position="bottom" padding="14px">
              <CryptoWalletIconComponent />
            </EmptyGridViewConnectAccountIcon>
          </EmptyGridViewConnectAccountView>
          {showAiSyncSubTiles && (
            <EmptyGridViewAiImportView isAddNew={props.isAddNewFlow} onClick={handleAiSyncTileClick}>
              <EmptyGridViewConnectAccountTitle
                position="top"
                padding="0 10px 12px 10px"
                margin="auto 0 0 0"
                fontWeight="400"
              >
                {i18n.t("assetsComponent.emptyViewAiSyncTitle")}
                <AiSyncRedDot preference="isAiSyncTilesTipShown" showIfUserBeforeThisTs={AI_SYNC_RELEASE_TS} />
              </EmptyGridViewConnectAccountTitle>
            </EmptyGridViewAiImportView>
          )}
        </EmptyGridViewTickerContainer>
      )}

      <EmptyGridViewTickerContainer
        width={readOnlyWlClient === true ? "550px" : props.isAddNewFlow ? "204px" : "252px"}
      >
        <EmptyGridViewConnectAccountView
          isColumnLayout={true}
          border="none"
          onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_CRYPTO)}
        >
          <EmptyGridViewConnectAccountTitle position="top" padding="0 0 12px 14px" margin="auto 0 0 0">
            {i18n.t("assetsComponent.emptyViewConnectCoinTitle")}
          </EmptyGridViewConnectAccountTitle>
        </EmptyGridViewConnectAccountView>
      </EmptyGridViewTickerContainer>

      <EmptyGridViewConnectExtrasContainer
        onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_HOMES)}
        width="110px"
      >
        <EmptyGridViewConnectExtrasIcon position="left" padding="12px">
          <HomesIconComponent />
        </EmptyGridViewConnectExtrasIcon>
        <EmptyGridViewConnectAccountTitle position="top" padding="6px 0 12px 12px">
          {i18n.t("assetsComponent.emptyViewConnectHomesTitle")}
        </EmptyGridViewConnectAccountTitle>
      </EmptyGridViewConnectExtrasContainer>

      <EmptyGridViewConnectExtrasContainer
        onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_CARS)}
        width="110px"
      >
        <EmptyGridViewConnectExtrasIcon position="right" padding="12px">
          <CarsIconComponent />
        </EmptyGridViewConnectExtrasIcon>
        <EmptyGridViewConnectAccountTitle position="top" padding="6px 0 12px 12px">
          {i18n.t("assetsComponent.emptyViewConnectCarsTitle")}
        </EmptyGridViewConnectAccountTitle>
      </EmptyGridViewConnectExtrasContainer>

      <EmptyGridViewConnectExtrasContainer
        onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_METAL)}
        width="110px"
      >
        <EmptyGridViewConnectExtrasIcon position="right" padding="5px 12px 12px 12px">
          <MetalsIconComponent />
        </EmptyGridViewConnectExtrasIcon>
        <EmptyGridViewConnectAccountTitle position="top" padding="6px 0 12px 12px">
          {i18n.t("assetsComponent.emptyViewConnectMetalsTitle")}
        </EmptyGridViewConnectAccountTitle>
      </EmptyGridViewConnectExtrasContainer>

      <EmptyGridViewTickerContainer width={props.isAddNewFlow ? "204px" : "252px"} border="none">
        <EmptyGridViewConnectAccountView
          isColumnLayout={true}
          onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_AI_APPRAISER)}
        >
          <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow} padding="12px 5px 0 14px">
            {i18n.t("assetsComponent.emptyViewConnectAiAppraiserSubtitle")}
          </EmptyGridViewConnectAccountSubtitle>
          <ConnectTitleIconContainer>
            <EmptyGridViewConnectAccountTitle position="top" padding="3px 0 12px 14px">
              {i18n.t("assetsComponent.emptyViewConnectAiAppraiserTitle")}
            </EmptyGridViewConnectAccountTitle>
            <EmptyGridViewConnectAccountIcon position="bottom" padding="0px 14px 14px 0px">
              <AiAppraiserIconComponent />
            </EmptyGridViewConnectAccountIcon>
          </ConnectTitleIconContainer>
        </EmptyGridViewConnectAccountView>
      </EmptyGridViewTickerContainer>

      {props.isReadOnlyWlClient === false && (
        <EmptyGridViewLinkPortfoliosView
          data-cy="addAssetLinkPortfolio"
          onClick={e => props.handleInitialTipClick(contextMenuItemType.CONNECT_PORTFOLIOS)}
          height="100%"
        >
          <EmptyGridViewConnectAccountTitle padding="0 14px 0 0">
            {i18n.t("assetsComponent.emptyViewConnectPortfolios")}
          </EmptyGridViewConnectAccountTitle>
          <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow}>
            {i18n.t("assetsComponent.emptyViewConnectPortfoliosSubtitle")}
            <DetailsLink
              isAddNew={props.isAddNewFlow}
              href="https://help.kubera.com/article/122-nested-portfolios"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {i18n.t("details")}
            </DetailsLink>
          </EmptyGridViewConnectAccountSubtitle>
          <EmptyGridViewConnectAccountIcon>
            <PortfolioLinkingIconComponent />
          </EmptyGridViewConnectAccountIcon>
        </EmptyGridViewLinkPortfoliosView>
      )}

      <EmptyGridViewManualItemsContainer>
        <EmptyGridViewManualTitle>{i18n.t("assetsComponent.emptyViewManualTitle")}</EmptyGridViewManualTitle>
        <EmptyGridViewBottomRowContainer isAddNew={props.isAddNewFlow}>
          <EmptyGridViewManualDesc isAddNew={props.isAddNewFlow}>
            {i18n.t("assetsComponent.emptyViewManualDesc")}
          </EmptyGridViewManualDesc>

          <EmptyGridViewTickerContainer width={"150px"} border="none">
            <EmptyGridViewManualView
              isAddNew={props.isAddNewFlow}
              border="none"
              isColumnLayout={true}
              onClick={e => props.handleInitialTipClick(contextMenuItemType.ENTER_QTY_PRICE)}
            >
              <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow} padding="12px 10px 10px 14px">
                {i18n.t("assetsComponent.addPVSTManuallySubtitle")}
              </EmptyGridViewConnectAccountSubtitle>
              <EmptyGridViewConnectAccountTitle position="top" padding="0 0 12px 14px">
                {i18n.t("assetsComponent.addPVSTManuallyTitle")}
              </EmptyGridViewConnectAccountTitle>
            </EmptyGridViewManualView>
          </EmptyGridViewTickerContainer>

          <EmptyGridViewTickerContainer border="none">
            <EmptyGridViewManualView
              borderRight="none"
              data-cy="addAssetManualEntry"
              isAddNew={props.isAddNewFlow}
              border="none"
              isColumnLayout={true}
              onClick={() => {
                if (!userPreferences.isAddAssetsManuallyTipShown) {
                  setShowManualAssetTip(true);
                } else {
                  props.handleInitialTipClick(null);
                }
              }}
            >
              <EmptyGridViewConnectAccountSubtitle isAddNew={props.isAddNewFlow} padding="12px 10px 10px 14px">
                {i18n.t("assetsComponent.addViewManualEntrySubtitle")}
              </EmptyGridViewConnectAccountSubtitle>
              <EmptyGridViewConnectAccountTitle position="top" padding="0 0 12px 14px">
                {i18n.t("assetsComponent.addViewManualEntryTitle")}
              </EmptyGridViewConnectAccountTitle>
            </EmptyGridViewManualView>
            {aiImportDisabled === false && (
              <EmptyGridViewAiImportView
                background="none"
                isAddNew={props.isAddNewFlow}
                onClick={e => props.handleInitialTipClick(contextMenuItemType.AI_DOCUMENT_IMPORT)}
              >
                <EmptyGridViewConnectAccountSubtitle
                  isAddNew={props.isAddNewFlow}
                  padding="14px 14px 0 14px"
                  margin="35px 0 0 0"
                >
                  {i18n.t("assetsComponent.emptyViewManualAiImportTitle")}
                </EmptyGridViewConnectAccountSubtitle>
                <EmptyGridViewConnectAccountTitle position="top" padding="6px 0 12px 14px" fontWeight="400">
                  {i18n.t("assetsComponent.emptyAiDocumentImportTitle")}
                </EmptyGridViewConnectAccountTitle>
              </EmptyGridViewAiImportView>
            )}
          </EmptyGridViewTickerContainer>
        </EmptyGridViewBottomRowContainer>
      </EmptyGridViewManualItemsContainer>

      {props.isAddNewFlow && (
        <AddViewEnterManuallyTip
          dangerouslySetInnerHTML={{
            __html: i18n.t("assetsComponent.addViewManuallyDesc2")
          }}
        />
      )}

      {showManualAssetTip && (
        <GenericMessageDialog
          dialogWidth="600px"
          description={i18n.t("assetsComponent.addManualEntryTip")}
          onOverlayDismissClick={() => setShowManualAssetTip(false)}
          positiveButtonTitle={i18n.t("continue")}
          positiveButtonWidth="110px"
          onPositiveButtonClick={() => {
            dispatch(
              updateUserPreferences({
                isAddAssetsManuallyTipShown: true
              })
            );
            props.handleInitialTipClick(null);
          }}
        />
      )}
    </EmptyGridView>
  );
}

export default EmptyGridViewAssetsComponent;
